import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface PpvMenuCategory {
    id: number;
    slug: string;
    name: string;
    description?: string | null;
    active: boolean;
    sortOrder: number;
    menuItemsCount?: number;
}

export interface PpvMenuItem {
    onlyfansId: number;
    id: string;
    command: string;
    name: string;
    message: string;
    inventoryId: number;
    inventorySubId: number;
    sentCount: number;
    purchaseCount: number;
    totalPurchaseAmount: number;
    copyCount?: number;
    /** Present only when the Cognito user is an admin (dashboard API). */
    boostScore?: number;
    public: boolean;
    active: boolean;
    sortOrder: number;
    lastSentAt: string | null;
    expireAt: string | null;
    aliases: string[];
    categoryIds: number[];
    previewImageKey: string | null;
    previewImageUrl: string | null;
}

export interface PpvMenuSalesTotal {
    onlyfansId: number;
    totalPurchaseAmount: number;
}

export interface UpsertPpvMenuItemPayload {
    command: string;
    aliases: string[];
    inventoryId: number;
    inventorySubId: number;
    name: string;
    message: string;
    categoryIds: number[];
    /** Public preview S3 key from POST .../menu/preview-image; null clears. */
    previewImageKey?: string | null;
    public: boolean;
    active: boolean;
    /** Set only when reordering; omitted on create/edit form (server appends new items to the end). */
    sortOrder?: number;
    expireAt: string | null;
    /** Admin-only: 0–100 directory boost (ignored for non-admin requests). */
    boostScore?: number;
}

export type MenuPreviewImageUploadResult =
    | { ok: true; previewImageKey: string; previewImageUrl: string }
    | { ok: false; status: 'rejected'; reason: string }
    | { ok: false; status: 'error'; message: string };

const useMenu = () => {
    const settingsContext = useContext(SettingsContext);
    const userContext = useContext(UserContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();

    const getMenuItems = async (): Promise<PpvMenuItem[]> => {
        const query = `${settingsContext.routes.menu.base}${params.userId}/menu/items`;
        return axios
            .get(query)
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);
                return [];
            });
    };

    const getMenuCategories = async (): Promise<PpvMenuCategory[]> => {
        return axios
            .get(settingsContext.routes.menu.categories)
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);
                return [];
            });
    };

    const createMenuItem = async (payload: UpsertPpvMenuItemPayload): Promise<PpvMenuItem | null> => {
        const query = `${settingsContext.routes.menu.base}${params.userId}/menu/items`;
        return axios
            .post(query, payload)
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);
                return null;
            });
    };

    const updateMenuItem = async (menuItemId: string, payload: Partial<UpsertPpvMenuItemPayload>): Promise<PpvMenuItem | null> => {
        const query = `${settingsContext.routes.menu.base}${params.userId}/menu/items/${menuItemId}`;
        return axios
            .put(query, payload)
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);
                return null;
            });
    };

    const deleteMenuItem = async (menuItemId: string): Promise<boolean> => {
        const query = `${settingsContext.routes.menu.base}${params.userId}/menu/items/${menuItemId}`;
        return axios
            .delete(query)
            .then(() => true)
            .catch(error => {
                handleHttpError(error, dialog);
                return false;
            });
    };

    const createMenuItemSale = async (menuItemId: string, amount: number, soldAt?: string): Promise<boolean> => {
        const query = `${settingsContext.routes.menu.base}${params.userId}/menu/sales`;
        return axios
            .post(query, { menuItemId: Number(menuItemId), amount, ...(soldAt ? { soldAt } : {}) })
            .then(() => true)
            .catch(error => {
                handleHttpError(error, dialog);
                return false;
            });
    };

    const getMenuSalesTotal = async (): Promise<PpvMenuSalesTotal> => {
        const query = `${settingsContext.routes.menu.base}${params.userId}/menu/sales/total`;
        return axios
            .get(query)
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);
                return { onlyfansId: 0, totalPurchaseAmount: 0 };
            });
    };

    const uploadMenuPreviewImage = async (
        file: File,
        onUploadProgress?: (loaded: number, total: number) => void,
    ): Promise<MenuPreviewImageUploadResult> => {
        const url = `${settingsContext.routes.menu.base}${params.userId}/menu/preview-image`;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await axios.post(url, formData, {
                onUploadProgress: ev => {
                    if (ev.total != null && onUploadProgress) {
                        onUploadProgress(ev.loaded, ev.total);
                    }
                },
            });
            const data = response.data as {
                status?: string;
                previewImageKey?: string;
                previewImageUrl?: string;
            };
            if (data?.status === 'accepted' && data.previewImageKey && data.previewImageUrl) {
                return { ok: true, previewImageKey: data.previewImageKey, previewImageUrl: data.previewImageUrl };
            }
            return { ok: false, status: 'error', message: 'Unexpected response from server.' };
        } catch (error: unknown) {
            const ax = error as { response?: { status?: number; data?: { reason?: string; message?: string } } };
            if (ax.response?.status === 422 && ax.response.data?.reason) {
                return { ok: false, status: 'rejected', reason: ax.response.data.reason };
            }
            await handleHttpError(error, dialog);
            const message = ax.response?.data?.message || (error instanceof Error ? error.message : 'Upload failed.') || 'Upload failed.';
            return { ok: false, status: 'error', message };
        }
    };

    const menuItemsQuery = useQuery(['ppvMenuItems', params.userId], getMenuItems, {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
    });

    const menuCategoriesQuery = useQuery(['ppvMenuCategories'], getMenuCategories, {
        staleTime: 60 * 1000 * 5,
        refetchOnWindowFocus: false,
        enabled: !!(userContext.jwtToken && settingsContext.apiKey),
    });

    const menuSalesTotalQuery = useQuery(['ppvMenuSalesTotal', params.userId], getMenuSalesTotal, {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
    });

    return {
        menuItemsQuery,
        menuCategoriesQuery,
        menuSalesTotalQuery,
        createMenuItem,
        updateMenuItem,
        deleteMenuItem,
        createMenuItemSale,
        uploadMenuPreviewImage,
    };
};

export default useMenu;
